import { useCallback, useEffect } from 'react';
import { MentionItem } from '../types';

interface UseKeyboardNavigationProps {
  isMenuOpen: boolean;
  items: MentionItem[];
  selectedIndex: number;
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  onNavigateBack?: () => void;
  canNavigateBack?: boolean;
  setSelectedIndex: (index: number) => void;
}

export const useKeyboardNavigation = ({
  isMenuOpen,
  items,
  selectedIndex,
  onSelect,
  onClose,
  onNavigateBack,
  canNavigateBack,
  setSelectedIndex
}: UseKeyboardNavigationProps) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isMenuOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(selectedIndex < items.length - 1 ? selectedIndex + 1 : 0);
        break;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : items.length - 1);
        break;

      case 'Enter':
        event.preventDefault();
        if (items[selectedIndex]) {
          onSelect(items[selectedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        onClose();
        break;

      case 'ArrowLeft':
        if (canNavigateBack && onNavigateBack) {
          event.preventDefault();
          onNavigateBack();
        }
        break;

      case 'Tab':
        event.preventDefault();
        onClose();
        break;

      default:
        break;
    }
  }, [
    isMenuOpen,
    items,
    selectedIndex,
    onSelect,
    onClose,
    onNavigateBack,
    canNavigateBack,
    setSelectedIndex
  ]);

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isMenuOpen, handleKeyDown]);

  return { handleKeyDown };
};
